import camelcaseKeys from 'camelcase-keys';
import queryString from 'query-string';
import type { Configuration } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { IRequest } from '../../request/Types';
import type {
  OsmGeoJsonFeature,
  OsmWayEnhancedByIdRequesterParams,
  OsmWayEnhancedItem,
  OsmWayEnhancedNearestRequesterParams,
  OsmWayEnhancedPaginatedRequesterParams,
  OsmWayEnhancedPaginatedResponse,
  OsmWayEnhancedUpsertPayload,
} from './APITypes';
import { osmWayToFeature } from './osmWayEnchancedTransformer';

export class OsmWayEnhancedRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getOsmWayEnhancedNearest = ({ lat, lng }: OsmWayEnhancedNearestRequesterParams): Promise<OsmGeoJsonFeature> => {
    const requestData: IRequest = {
      endpoint: `/osm-way-enhanced/nearest?lat=${lat}&lon=${lng}`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => {
      const responseData = camelcaseKeys(response.data, { deep: true });
      return osmWayToFeature(responseData);
    });
  };

  public getOsmWayEnhancedById = ({ wayId }: OsmWayEnhancedByIdRequesterParams): Promise<OsmWayEnhancedItem> => {
    const requestData: IRequest = {
      endpoint: `/osm-way-enhanced/${wayId}`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => {
      const responseData = camelcaseKeys(response.data, { deep: true });
      const geojson = osmWayToFeature(responseData);
      return { ...responseData, geojson };
    });
  };

  public getOsmWayEnhancedHistoryByWayId = ({ wayId }: OsmWayEnhancedByIdRequesterParams): Promise<any> => {
    const requestData: IRequest = {
      endpoint: `/osm-way-enhanced/${wayId}/history`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true });
    });
  };

  public getOsmWayEnhancedByGeoJson = ({ geoJson }: { geoJson: any }): Promise<OsmGeoJsonFeature[]> => {
    const requestData: IRequest = {
      endpoint: '/osm-way-enhanced/geojson',
      method: HttpMethod.POST,
      body: JSON.stringify(geoJson),
    };

    return this.requester.request(requestData).then((response) => {
      const responseData = camelcaseKeys(response.data, { deep: true });
      return responseData.map((osmWay: any) => osmWayToFeature(osmWay));
    });
  };

  public getOsmWaysPaginated = (params: OsmWayEnhancedPaginatedRequesterParams): Promise<OsmWayEnhancedPaginatedResponse> => {
    const { search } = params;
    const queryParams = queryString.stringify({ ...params, search: search || '' }, { skipNull: true, skipEmptyString: true });

    const requestData: IRequest = {
      endpoint: `/osm-way-enhanced/custom-speed?${queryParams}`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true });
    });
  };

  public upsertOsmWayEnhanced = async (payload: OsmWayEnhancedUpsertPayload[]): Promise<any> => {
    const payloadParsed = payload.map((item) => ({
      way_id: Number.parseInt(item.wayId),
      speed_limit_in_kmh: item.speedLimitInKmh,
      speed_limit_heavy_in_kmh: item.speedLimitHeavyInKmh,
      description: item.description,
      reason: item.reason,
      active: item?.active ?? undefined,
    }));

    const requestData: IRequest = {
      endpoint: '/osm-way-enhanced',
      method: HttpMethod.POST,
      body: JSON.stringify(payloadParsed),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data, { deep: true });
  };
}

export default OsmWayEnhancedRequester;
